import { useState } from "react";

import { fetchArtifactsForRag } from "../../../data/queries/artifacts";
import { Artifact, ArtifactType } from "../../../types/artifact";
import { convertToJson, copyJsonToClipboard } from "../../../utils/json";
import { INITIAL_INDEX, SINGLE_SELECTION } from "../constants/numeric";
import type { NotificationState } from "./notification";

const RAG_FETCH_PAGE = 0;
const RAG_FETCH_PAGE_SIZE = 1000;

const COPY_ARTIFACT_TYPES = [
  ArtifactType.Summary,
  ArtifactType.Transcript,
  ArtifactType.Scorecard,
  ArtifactType.CallDataRecord,
  ArtifactType.CallRecordingEvent,
  ArtifactType.CallTranscriptionRecord
];

interface UseExploreClipboardParams {
  artifacts: Artifact[];
  rowSelectionModel: readonly (string | number)[];
  getRowKey: (row: Artifact) => string;
  permittedEmails: string[] | null;
}

export interface UseExploreClipboardResult {
  isJsonCopying: boolean;
  notification: NotificationState;
  handleCopyJson: () => Promise<void>;
  handleCloseNotification: (_event?: React.SyntheticEvent | Event, reason?: string) => void;
}

const buildSuccessMessage = (count: number): string =>
  `Copied ${count} ${count === SINGLE_SELECTION ? "interaction" : "interactions"} to clipboard in JSON format`;

interface ExecuteCopyParams {
  artifacts: Artifact[];
  rowSelectionModel: readonly (string | number)[];
  getRowKey: (row: Artifact) => string;
  permittedEmails: string[] | null;
  setNotification: (state: NotificationState) => void;
}

const executeCopy = async (params: ExecuteCopyParams): Promise<void> => {
  const { artifacts, rowSelectionModel, getRowKey, permittedEmails, setNotification } = params;
  const selectedArtifacts = artifacts.filter((artifact) => rowSelectionModel.includes(getRowKey(artifact)));
  const interactionIds = selectedArtifacts.map((artifact) => artifact.interaction_id);
  const result = await fetchArtifactsForRag(
    { interactionIds, page: RAG_FETCH_PAGE, pageSize: RAG_FETCH_PAGE_SIZE, permittedEmails },
    COPY_ARTIFACT_TYPES
  );
  if (result.error || !result.data) {
    const msg = result.error instanceof Error ? result.error.message : "Failed to fetch artifact data";
    setNotification({ open: true, message: `Error copying to clipboard: ${msg}`, severity: "error" });
    return;
  }
  const success = await copyJsonToClipboard(convertToJson(result.data));
  if (!success) {
    setNotification({ open: true, message: "Error copying to clipboard: Failed to copy data", severity: "error" });
    return;
  }
  setNotification({ open: true, message: buildSuccessMessage(interactionIds.length), severity: "success" });
};

interface BuildCopyHandlerParams {
  artifacts: Artifact[];
  rowSelectionModel: readonly (string | number)[];
  getRowKey: (row: Artifact) => string;
  permittedEmails: string[] | null;
  setIsJsonCopying: (copying: boolean) => void;
  setNotification: (state: NotificationState) => void;
}

const buildCopyHandler = (params: BuildCopyHandlerParams) => async (): Promise<void> => {
  const { artifacts, rowSelectionModel, getRowKey, permittedEmails, setIsJsonCopying, setNotification } = params;
  if (rowSelectionModel.length === INITIAL_INDEX) {
    return;
  }
  setIsJsonCopying(true);
  try {
    await executeCopy({ artifacts, rowSelectionModel, getRowKey, permittedEmails, setNotification });
  } catch (error) {
    const errMsg = error instanceof Error ? error.message : "Unknown error";
    setNotification({ open: true, message: `Error copying to clipboard: ${errMsg}`, severity: "error" });
  } finally {
    setIsJsonCopying(false);
  }
};

export const useExploreClipboard = ({
  artifacts, rowSelectionModel, getRowKey, permittedEmails
}: UseExploreClipboardParams): UseExploreClipboardResult => {
  const [isJsonCopying, setIsJsonCopying] = useState(false);
  const [notification, setNotification] = useState<NotificationState>({ open: false, message: "", severity: "success" });

  const handleCopyJson = buildCopyHandler({
    artifacts, rowSelectionModel, getRowKey, permittedEmails, setIsJsonCopying, setNotification
  });

  const handleCloseNotification = (_event?: React.SyntheticEvent | Event, reason?: string): void => {
    if (reason === "clickaway") {
      return;
    }
    setNotification(prev => ({ ...prev, open: false }));
  };

  return { isJsonCopying, notification, handleCopyJson, handleCloseNotification };
};
